import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu } from 'reactstrap';
import { shouldDeferFirstTabRedirect, usePostAuthLandingGate } from '../login/postAuthLandingGate';
import MenuDropdownItem from './menuDropdownItem';
import Icon from '../common/containers/icon';
import AdminMenu from './adminMenu';
import checkCabinetPermissions from './headerUtils';
import processLegacyCabinets from './legacyCabinets';
import CabinetMenuItem from './cabinetMenuItem';
import DirectFormMenuItem from './directFormMenuItem';
import appHistory from '../../utils/appHistory';
import { getTabGroupSortOrder, hasAdminTabGroupCabinets } from './navItems';
const processCabinetType = (dashboards, user, groupedCabinets, ungroupedCabinets) => {
  const processCabinet = cabinet => {
    const {
      identifier,
      title,
      path,
      client: {
        web: {
          layout: {
            superset
          } = {},
          icon = 'folder-open',
          tabGroup = {},
          sortOrder = 0
        } = {}
      } = {},
      directFormType
    } = cabinet;
    const isValidDashboards = dashboards.some(dashboard => dashboard.name === superset);
    const isInvalidSuperset = superset && !isValidDashboards;
    const hasCabinetPermission = checkCabinetPermissions(cabinet, user);
    if (!isInvalidSuperset && hasCabinetPermission) {
      if (tabGroup.identifier) {
        if (!groupedCabinets[tabGroup.identifier]) {
          // eslint-disable-next-line no-param-reassign
          groupedCabinets[tabGroup.identifier] = [];
        }
        groupedCabinets[tabGroup.identifier].push({
          identifier,
          title,
          path,
          icon,
          sortOrder,
          directFormType
        });
      } else if (!tabGroup.identifier) {
        ungroupedCabinets.push({
          identifier,
          title,
          path,
          icon,
          sortOrder,
          directFormType
        });
      }
    }
  };
  return processCabinet;
};

// Helper function to get tab group info for a cabinet
const getTabGroupInfo = (tabGroupIdentifier, tabGroups) => {
  if (!tabGroups || !tabGroupIdentifier) return null;
  return tabGroups.find(group => group.identifier === tabGroupIdentifier);
};
const GroupDropdown = ({
  tabGroupIdentifier,
  tabGroups,
  groupedCabinets,
  showIcons: _showIcons = true
}) => {
  const groupInfo = getTabGroupInfo(tabGroupIdentifier, tabGroups);
  if (!groupInfo || !(groupedCabinets !== null && groupedCabinets !== void 0 && groupedCabinets.length)) return null;
  const sortedCabinets = groupedCabinets.sort((a, b) => a.sortOrder - b.sortOrder);
  const groupDropdownItems = sortedCabinets.map(cabinet => {
    if (cabinet.directFormType) {
      return /*#__PURE__*/React.createElement(DirectFormMenuItem, {
        key: cabinet.identifier,
        identifier: cabinet.identifier,
        title: cabinet.title,
        icon: cabinet.icon,
        directFormType: cabinet.directFormType,
        showIcons: _showIcons
      });
    }
    return /*#__PURE__*/React.createElement(MenuDropdownItem, {
      key: cabinet.identifier,
      className: `t-h-nav-${cabinet.identifier}`,
      title: cabinet.title,
      glyph: _showIcons ? cabinet.icon : '',
      href: `/#${cabinet.path}`
    });
  });
  return /*#__PURE__*/React.createElement(UncontrolledDropdown, {
    nav: true,
    inNavbar: true,
    className: `t-h-nav-${tabGroupIdentifier}-dropdown`,
    key: `${tabGroupIdentifier}Dropdown`,
    id: `${tabGroupIdentifier}DropdownMenu`,
    style: {
      textAlign: 'center'
    }
  }, /*#__PURE__*/React.createElement(DropdownToggle, {
    nav: true,
    caret: true
  }, _showIcons && /*#__PURE__*/React.createElement(Icon, {
    glyph: groupInfo.icon || 'folder'
  }), _showIcons && /*#__PURE__*/React.createElement("br", null), groupInfo.title), /*#__PURE__*/React.createElement(DropdownMenu, null, groupDropdownItems));
};
const HierarchicalHeader = ({
  user,
  cabinets: _cabinets = [],
  dashboards: _dashboards = [],
  tabGroups: _tabGroups = [],
  settingsLoaded
}) => {
  const showIcons = true; // Top nav always shows icons (layout+mode selector controls shell/theme)
  const {
    pathname
  } = appHistory.location;
  const landingGatePhase = usePostAuthLandingGate();
  const navMenuItems = [];

  // Group cabinets by their groups
  const groupedCabinets = {};
  const ungroupedCabinets = [];
  const cabinetsWithPath = _cabinets.length > 0 ? processLegacyCabinets(_cabinets) : [];
  cabinetsWithPath.forEach(processCabinetType(_dashboards, user, groupedCabinets, ungroupedCabinets));

  // A real Archive "File Cabinet" resolves to /file-cabinet; when one is visible it is a
  // genuine destination, not a home alias to bounce away from to the first form cabinet.
  const hasLegacyFileCabinet = ungroupedCabinets.some(cabinet => cabinet.path === '/file-cabinet') || Object.values(groupedCabinets).some(group => group.some(cabinet => cabinet.path === '/file-cabinet'));
  let firstCabinet = null;
  ungroupedCabinets.sort((a, b) => a.sortOrder - b.sortOrder).forEach(cabinet => {
    if (!firstCabinet) firstCabinet = cabinet;
    if (cabinet.directFormType) {
      navMenuItems.push(/*#__PURE__*/React.createElement(DirectFormMenuItem, {
        key: cabinet.identifier,
        identifier: cabinet.identifier,
        title: cabinet.title,
        icon: cabinet.icon,
        directFormType: cabinet.directFormType,
        showIcons: showIcons
      }));
    } else {
      navMenuItems.push(/*#__PURE__*/React.createElement(CabinetMenuItem, {
        key: cabinet.identifier,
        identifier: cabinet.identifier,
        title: cabinet.title,
        icon: cabinet.icon,
        path: cabinet.path,
        showIcons: showIcons
      }));
    }
  });
  const sortedGroupedCabinets = Object.entries(groupedCabinets).sort((a, b) => {
    const orderDiff = getTabGroupSortOrder(a[0], _tabGroups) - getTabGroupSortOrder(b[0], _tabGroups);
    if (orderDiff !== 0) return orderDiff;
    return a[0].localeCompare(b[0]);
  });
  sortedGroupedCabinets.forEach(tabGroup => {
    const tabGroupIdentifier = tabGroup[0];
    const tabGroupCabinets = tabGroup[1];
    // Skip 'admin' tab group: AdminMenu below already shows admin cabinets + hardcoded sections
    if (tabGroupIdentifier === 'admin' || !(tabGroupCabinets !== null && tabGroupCabinets !== void 0 && tabGroupCabinets.length)) {
      return;
    }
    navMenuItems.push(/*#__PURE__*/React.createElement(GroupDropdown, {
      key: tabGroupIdentifier,
      tabGroupIdentifier: tabGroupIdentifier,
      tabGroups: _tabGroups,
      groupedCabinets: tabGroupCabinets,
      showIcons: showIcons
    }));
  });

  // Before CU-86e1krcxh (theming layout work), hierarchical nav had no Admin cog — only mount
  // AdminMenu when the account defines admin tab-group cabinets (e.g. sherwinmsc). Accounts
  // like superadmin route admin surfaces through other tab groups instead.
  if (hasAdminTabGroupCabinets(_cabinets)) {
    navMenuItems.push(/*#__PURE__*/React.createElement(AdminMenu, {
      key: "admin"
    }));
  }

  // Derive first-tab URL from cabinet data (avoids needing href on DirectFormMenuItem)
  let firstVisibleTab = null;
  if (firstCabinet) {
    firstVisibleTab = firstCabinet.directFormType ? `/directForms/new/${firstCabinet.directFormType}` : firstCabinet.path;
  }
  useEffect(() => {
    const isLandingRoute = pathname === '/' || pathname === '/file-cabinet' && !hasLegacyFileCabinet;
    if (settingsLoaded && firstVisibleTab && isLandingRoute) {
      if (shouldDeferFirstTabRedirect()) {
        return;
      }
      appHistory.replace(firstVisibleTab);
    }
  }, [pathname, settingsLoaded, firstVisibleTab, landingGatePhase, hasLegacyFileCabinet]);
  return navMenuItems;
};
HierarchicalHeader.propTypes = {
  user: PropTypes.shape({
    permissions: PropTypes.shape({
      general: PropTypes.arrayOf(PropTypes.string)
    })
  }).isRequired,
  cabinets: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    path: PropTypes.string,
    denyPermissions: PropTypes.arrayOf(PropTypes.string),
    allowPermissions: PropTypes.arrayOf(PropTypes.string),
    client: PropTypes.shape({
      web: PropTypes.shape({
        layout: PropTypes.shape({
          superset: PropTypes.string
        }),
        icon: PropTypes.string,
        tabGroup: PropTypes.shape({
          identifier: PropTypes.string,
          sortOrder: PropTypes.number
        })
      })
    })
  })),
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string
  })),
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })),
  settingsLoaded: PropTypes.bool.isRequired
};
GroupDropdown.propTypes = {
  tabGroupIdentifier: PropTypes.string.isRequired,
  tabGroups: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })).isRequired,
  groupedCabinets: PropTypes.arrayOf(PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.string,
    sortOrder: PropTypes.number
  })).isRequired,
  showIcons: PropTypes.bool
};
export default HierarchicalHeader;